import type { Dictionary } from './types.js';

// ---------------------------------------------------------------------------
// One place that turns a publisher error category into words an operator can
// act on. Both Home (the attention row) and Review (the failure panel) need
// this, and they must not drift: a post described as "out of credit" on one
// screen and "rejected" on the other is worse than either label alone.
//
// The categories are `PublishErrorCategory` (packages/ports/src/publisher.ts).
// They are grouped, not mapped one-to-one, because the operator's *next
// action* is what the label has to convey, and several categories share one:
// `billing_or_quota` and `rate_limit` both mean "the account, not the post,
// is the problem"; `validation`, `duplicate` and `permanent_provider` all
// mean "the platform looked at this content and refused it".
//
// `authentication` is deliberately absent: a dead credential already has its
// own, higher-ranked attention item (`reconnect-required`), and naming it
// here as a mere publication failure would bury the reconnection the
// operator actually has to perform.
// ---------------------------------------------------------------------------

export const failureReasonLabel = (dict: Dictionary, category: string | null): string => {
  switch (category) {
    case 'billing_or_quota':
    case 'rate_limit':
      return dict.attentionReasons.creditOrRateLimit;
    case 'media':
      return dict.attentionReasons.mediaInvalid;
    case 'validation':
    case 'duplicate':
    case 'permanent_provider':
      return dict.attentionReasons.rejectedByPlatform;
    // `transient_provider`, `internal`, and an absent category all mean the
    // same thing to an operator: it failed and the cause is not one they can
    // name from here. The attempt history on the review screen carries the
    // raw message for those.
    default:
      return dict.attentionReasons.publicationFailed;
  }
};
